import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dna, Store, X, Plus, Pencil, Check } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const BUSINESS_TYPE_LABELS = { dogs: "Dogs", cats: "Cats", small: "Small animals", reptiles: "Reptiles", birds: "Birds" };
export const BUSINESS_FORMAT_LABELS = { online: "Online Sale", storefront: "Store Front" };
const SUGGESTIONS = ["Dog", "Cat", "Rabbit", "Guinea Pig", "Hamster", "Rat", "Ferret", "Chinchilla", "Budgie", "Cockatiel", "Parrot", "Canary",
  "Ball Python", "Corn Snake", "Bearded Dragon", "Leopard Gecko", "Crested Gecko", "Tortoise", "Goldfish", "Betta", "Guppy", "Chicken", "Duck",
  "Goat", "Sheep", "Pig", "Horse", "Alpaca", "Golden Retriever", "Labrador Retriever", "German Shepherd", "French Bulldog", "Poodle",
  "Goldendoodle", "Bernese Mountain Dog", "Siberian Husky", "Dachshund", "Shih Tzu", "Maine Coon", "Ragdoll", "Bengal", "Siamese", "British Shorthair", "Sphynx"];

const Chip = ({ children, onRemove, testid, tone = "purple" }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${tone === "purple" ? "bg-purple-50 text-purple-700 border border-purple-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
    {children}
    {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${children}`} className="hover:text-red-600"><X size={12} /></button>}
  </span>
);

const save = async (body, onSaved) => {
  try { await api.put("/auth/me/profile-page", body); toast.success("Saved"); onSaved(); return true; }
  catch (e) { toast.error(errText(e)); return false; }
};

export const BreederSpecies = ({ data, mine, onReload }) => {
  const [editing, setEditing] = useState(false);
  const [list, setList] = useState(data.breeder_species || []);
  const [text, setText] = useState("");
  const sugg = useMemo(() => {
    const q = text.trim().toLowerCase();
    return q ? SUGGESTIONS.filter((s) => s.toLowerCase().includes(q) && !list.some((l) => l.toLowerCase() === s.toLowerCase())).slice(0, 6) : [];
  }, [text, list]);
  if (!data.verified_breeder) return null;
  const current = data.breeder_species || [];
  if (!mine && !current.length) return null;
  const add = (v) => {
    const val = v.trim().slice(0, 40);
    if (!val || list.some((l) => l.toLowerCase() === val.toLowerCase())) return setText("");
    if (list.length >= 20) return toast.error("You can list up to 20 species");
    setList([...list, val]); setText("");
  };
  return (
    <div className="mt-4" data-testid="breeder-species">
      <div className="flex items-center gap-2 mb-2">
        <Dna size={15} className="text-purple-600" />
        <span className="text-sm font-bold text-[#12234A]">Species I breed</span>
        {mine && !editing && <button type="button" onClick={() => { setList(current); setEditing(true); }} className="text-xs text-purple-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid="breeder-species-edit"><Pencil size={12} /> {current.length ? "Edit" : "Add species"}</button>}
      </div>
      {!editing ? (
        <div className="flex flex-wrap gap-1.5">{current.map((s) => <Chip key={s} testid={`breeder-species-chip-${s}`}>{s}</Chip>)}</div>
      ) : (
        <div className="rounded-xl border border-purple-200 bg-white p-3 max-w-xl">
          <div className="flex flex-wrap gap-1.5 mb-2">{list.map((s) => <Chip key={s} onRemove={() => setList(list.filter((x) => x !== s))}>{s}</Chip>)}</div>
          <div className={`relative flex gap-2 ${sugg.length ? "mb-40" : ""}`}>
            <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={40} disabled={list.length >= 20}
              onBlur={() => setTimeout(() => setText((v) => (sugg.includes(v) ? "" : v)), 150)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); } }}
              placeholder={list.length >= 20 ? "Limit of 20 reached" : "Type a species or breed, press Enter"} className="h-9 rounded-lg" data-testid="breeder-species-input" />
            <Button type="button" size="sm" variant="outline" onClick={() => add(text)} className="h-9 rounded-lg" data-testid="breeder-species-add"><Plus size={14} /></Button>
            {sugg.length > 0 && (
              <div className="absolute top-10 left-0 z-20 w-64 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-40 overflow-y-auto" data-testid="breeder-species-suggestions">
                {sugg.map((s) => <button key={s} type="button" onClick={() => add(s)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-purple-50">{s}</button>)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={async () => { if (await save({ breeder_species: list }, onReload)) setEditing(false); }} className="rounded-lg bg-purple-600 hover:bg-purple-700 gap-1" data-testid="breeder-species-save"><Check size={14} /> Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-lg">Cancel</Button>
            <span className="text-xs text-slate-400 ml-auto">{list.length}/20</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const BusinessTypes = ({ data, mine, onReload }) => {
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(data.business_types || []);
  if (!data.verified_business && !data.verified_breeder) return null;
  const current = data.business_types || [];
  if (!mine && !current.length) return null;
  const keys = Object.keys(BUSINESS_TYPE_LABELS);
  const all = keys.every((k) => sel.includes(k));
  const toggle = (k) => setSel(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);
  const pill = (on) => `h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"}`;
  return (
    <div className="mt-4" data-testid="business-types">
      <div className="flex items-center gap-2 mb-2">
        <Store size={15} className="text-amber-600" />
        <span className="text-sm font-bold text-[#12234A]">Type of business</span>
        {mine && !editing && <button type="button" onClick={() => { setSel(current); setEditing(true); }} className="text-xs text-amber-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid="business-types-edit"><Pencil size={12} /> {current.length ? "Edit" : "Choose"}</button>}
      </div>
      {!editing ? (
        <div className="flex flex-wrap gap-1.5">
          {keys.every((k) => current.includes(k)) ? <Chip tone="amber" testid="business-type-chip-all">All animals</Chip>
            : current.filter((k) => BUSINESS_TYPE_LABELS[k]).map((k) => <Chip key={k} tone="amber" testid={`business-type-chip-${k}`}>{BUSINESS_TYPE_LABELS[k]}</Chip>)}
          {current.filter((k) => BUSINESS_FORMAT_LABELS[k]).map((k) => <Chip key={k} tone="amber" testid={`business-type-chip-${k}`}>{BUSINESS_FORMAT_LABELS[k]}</Chip>)}
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-white p-3 max-w-xl">
          <div className="flex flex-wrap gap-2">
            {keys.map((k) => <button key={k} type="button" onClick={() => toggle(k)} className={pill(sel.includes(k))} data-testid={`business-type-${k}`}>{BUSINESS_TYPE_LABELS[k]}</button>)}
            <button type="button" onClick={() => setSel(all ? sel.filter((x) => !keys.includes(x)) : [...new Set([...sel, ...keys])])} className={pill(all)} data-testid="business-type-all">All</button>
          </div>
          <p className="text-xs font-semibold text-slate-500 mt-3 mb-1.5">How you sell</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(BUSINESS_FORMAT_LABELS).map((k) => <button key={k} type="button" onClick={() => toggle(k)} className={pill(sel.includes(k))} data-testid={`business-type-${k}`}>{BUSINESS_FORMAT_LABELS[k]}</button>)}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={async () => { if (await save({ business_types: sel }, onReload)) setEditing(false); }} className="rounded-lg bg-amber-500 hover:bg-amber-600 gap-1" data-testid="business-types-save"><Check size={14} /> Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-lg">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
